import { useEffect, useRef, useState } from 'react';

/**
 * Types a label in, backspaces the whole line out, then types the next.
 *
 * The activity timeline's header is a single line that gets swapped as the turn
 * moves between steps. Replacing the string outright reads as a flicker — the
 * eye registers that something changed but not what. Typing it makes the change
 * legible and doubles as a signal that the turn is still moving, which matters
 * because this header is the only live surface once the waiting indicator steps
 * aside.
 *
 * The whole line is cleared before the next one starts, rather than editing the
 * shared prefix in place. A partial rewrite is fewer keystrokes but reads as a
 * glitch — the eye cannot tell an edited tail from a flicker, whereas an empty
 * line followed by fresh typing is unmistakably "next thing now".
 *
 * Deliberately never shows a hybrid of two labels: during deletion the text is
 * always a prefix of what was on screen, and during typing always a prefix of
 * the target.
 */

// Deleting reads better fast — it is undoing, not saying something. Typing is
// slower because it is the part being read. Both are quick enough that a
// sentence-length label still clears and retypes between steps, which arrive
// seconds apart.
const DELETE_MS = 12;
const TYPE_MS = 22;
// Only a guard against an absurd label (a leaked paragraph, say) holding the
// line hostage; real step labels are well inside it and type in full.
const MAX_TYPED_CHARS = 160;

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {string} target - the label to display
 * @param {{enabled?: boolean, replayMs?: number}} [options] - enabled=false
 *        shows the target as-is. replayMs > 0 replays the animation (backspace
 *        out, retype) after the fully-typed label has sat unchanged that long,
 *        and keeps replaying while it stays unchanged — motion as a liveness
 *        signal when no new step arrives to change the label.
 * @returns {{text: string, animating: boolean}}
 */
export const useTypewriter = (target, { enabled = true, replayMs = 0 } = {}) => {
  const safeTarget = typeof target === 'string' ? target : '';
  const skip = !enabled || prefersReducedMotion();

  const [text, setText] = useState(skip ? safeTarget : '');
  const timerRef = useRef(null);
  // The rendered text, readable by the effect without making it a dependency —
  // otherwise every character typed would tear down and restart the animation.
  const textRef = useRef(text);
  textRef.current = text;

  useEffect(() => {
    if (skip) {
      setText(safeTarget);
      return undefined;
    }

    // The cursor lives here, in the effect, rather than being read back out of
    // rendered state each tick. React batches the updates a timer chain
    // produces, so a step that read state would keep seeing the same stale
    // string and the animation would stall after a character or two.
    // `textRef` is only the starting point: whatever is on screen right now, so
    // a target that changes mid-animation continues from the visible text
    // instead of restarting.
    let shown = textRef.current;
    // The phase has to be explicit. Deriving it per tick from "is the text the
    // target yet" cannot work: a half-typed label also differs from its target,
    // so the run would flip back to deleting after the first character.
    let phase = shown && shown !== safeTarget ? 'deleting' : 'typing';

    let replayTimer = null;

    // Backspace the settled label out and let tick retype it — the same
    // delete-then-type cycle a label change runs, so a replay is
    // indistinguishable from real motion.
    const replay = () => {
      phase = 'deleting';
      timerRef.current = setTimeout(tick, DELETE_MS);
    };

    const armReplay = () => {
      if (replayMs > 0 && safeTarget) replayTimer = setTimeout(replay, replayMs);
    };

    const tick = () => {
      if (phase === 'deleting') {
        shown = shown.slice(0, shown.length - 1);
        if (!shown) phase = 'typing';
      } else {
        // A guard for an absurd label only; ordinary ones type in full.
        shown = safeTarget.length > MAX_TYPED_CHARS
          ? safeTarget
          : safeTarget.slice(0, shown.length + 1);
      }
      setText(shown);
      if (shown !== safeTarget) {
        timerRef.current = setTimeout(tick, phase === 'deleting' ? DELETE_MS : TYPE_MS);
      } else {
        armReplay();
      }
    };

    // Scheduled rather than immediate, so a target that changes twice within one
    // tick never renders the intermediate label.
    if (shown !== safeTarget) timerRef.current = setTimeout(tick, TYPE_MS);
    else armReplay();
    return () => { clearTimeout(timerRef.current); clearTimeout(replayTimer); };
  }, [safeTarget, skip, replayMs]);

  return { text, animating: !skip && text !== safeTarget };
};
